import { useEffect, useState } from "react";
import ApiRoles from "../../../../api/roles";

/**
 * LF360-2024 — project roles in the user forms, as records.
 *
 * A project row holds the role by id, with what the form needs to draw it:
 * its name, whether it covers the whole project (then there are no sites to
 * pick) and its built-in key, if any. The options are the roles the signed-in
 * user may give (GET /v1/roles/assignable); a row whose role is not among
 * them — a wider role somebody else gave — still shows it, unselectable.
 */

/** The roles the signed-in user may give in the current project. */
export const useAssignableRoles = (enabled = true) => {
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    ApiRoles.getAssignableRoles()
      .then(({ data }) => live && setRoles(data?.roles || []))
      .catch(() => live && setRoles([]));
    return () => {
      live = false;
    };
  }, [enabled]);

  return roles;
};

const EMPTY = { roleId: null, roleName: null, roleSiteScoping: null, roleKey: null };

/** A row's role fields for a role picked from the options. */
export const rowRoleOf = (role) => (role
  ? { roleId: role.id, roleName: role.name, roleSiteScoping: role.siteScoping, roleKey: role.builtInKey || null }
  : EMPTY);

/** A row's role fields from a membership the server sent. */
export const rowRoleOfLink = (link) => ({
  roleId: link.roleId || null,
  roleName: link.roleName || null,
  roleSiteScoping: link.roleSiteScoping || null,
  roleKey: link.roleKey || null,
});

/** Whether the row's role covers the whole project, so sites do not limit it. */
export const coversWholeProject = (row) => row.roleSiteScoping === "whole";

/** Options for a row's role picker: the assignable roles, and the row's own. */
export const roleOptionsFor = (roles, row) => {
  const options = roles.map((role) => ({ value: role.id, label: role.name }));
  if (row?.roleId && !roles.some((role) => role.id === row.roleId)) {
    options.push({ value: row.roleId, label: row.roleName || "Current role", disabled: true });
  }
  return options;
};

/** What a project row sends to the server. */
export const rowPayload = (row) => ({ projectId: row.projectId, roleId: row.roleId, siteIds: row.siteIds });
